import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import _cloneDeep from 'lodash/cloneDeep';
import { ACCOMMODATION_STATUS } from 'src/config/AccommodationStatusForJmrUpdate';
import { AccommodationStatusIds } from 'src/config/AccommodationStatusId';
import CapacityUsageService from 'src/services/capacityUsage/capacityUsageService';
import { type SiteJobCapacityUsageData } from 'src/services/capacityUsage/types/siteJobCapacityUsageData';
import EmailDistributionService from 'src/services/emailDistribution/emailDistributionService';
import { type EmailSettings } from 'src/services/emailDistribution/types/emailSettings';
import IncidentService from 'src/services/incident/incidentService';
import JmrService from 'src/services/jmr/jmrService';
import {
  type AccommodationFields,
  type AllocatedJobShift,
  type AppliedModification,
  type JmrPayload,
  type JmrWorkRestriction,
  type NonPhysicalLimitation,
  YesNoEnum,
} from 'src/services/jmr/types';
import JobsService from 'src/services/jobsService';
import type { EmployeeInfo } from 'src/services/people/types';
import { getJobTitle } from 'src/services/people/utils';
import PeopleApiService from 'src/services/peopleApiService';
import { MeasureUnitValues } from 'src/services/tasks/constants';
import TaskService from 'src/services/tasks/taskService';
import { type RegionTask } from 'src/services/tasks/types';
import { ApiCallStatus } from 'src/store/type';
import { type ApiVersion } from 'src/types';
import { datePickerStringToDateObj } from 'src/utils/dateUtils';
import {
  isT2Admin,
  JmrFlowTypes,
  type JmrPageProgress,
  type JmrResult,
  jmrResultToTableRow,
  type TableDataRow,
} from 'src/utils/jmrUtils';
import { type RestrictionOption } from 'src/utils/restrictionUtils';
import { regionTaskListToRestrictionItemTasks } from 'src/utils/restrictionUtilsLegacy';

import type { RootState } from '../store';

import { initialState } from './constants';
import type { BackgroundFormData, FetchAustinCasesPayload, JmrTask } from './types';

// **** Create Async-Thunks **** //

/**
 * Fetch a list of Austin cases (incidents). Note that if we are "updating" the accommodation (placement), the "alias"
 * in the background page may not have access to the site (and by default not have access to the incidences) and we will
 * need to fetch the incident specific to that accommodation instead.
 */
export const fetchAustinCases = createAsyncThunk(
  'newJmr/fetchAustinCases',
  async (payload: FetchAustinCasesPayload, { dispatch }) => {
    dispatch(actions.clearAustinCases());
    if ('alias' in payload) {
      const resp = await IncidentService.fetchForAssociate(payload.alias);
      return resp?.data ?? [];
    } else if ('placementId' in payload) {
      const resp = await IncidentService.fetchForAccommodation(payload.placementId);
      return [resp?.data];
    }
    return [];
  },
);

/**
 * Get the employeee info
 */
export const getEmployeeInfo = createAsyncThunk('newJmr/getEmployeeInfo', async (employeeLogin: string) => {
  const { data } = await PeopleApiService.getEmployeeInfo(employeeLogin);
  return data;
});

/**
 * @note featureFlags.TASK_GROUPING "Off"
 *
 * Get tasks of a region by region Id
 */
export const getRegionTasksById = createAsyncThunk('newJmr/getRegionTasksById', (regionId: number) => {
  return TaskService.fetchRegionTasksByRegionId(regionId);
});

/**
 * Will result in setting the dpFeatureFlag to true if this async thunk action is dispatched.
 */
export const setDecisionPortalFeatureFlagInNewJmrSlice = createAsyncThunk(
  'newJmr/setDecisionPortalFeatureInNewJmrSlice',
  () => true,
);

/**
 * Fetch a job by the job-id
 */
export const getJob = createAsyncThunk('newJmr/getJob', async (jobId: number) => {
  const { data } = await JobsService.getJobById(jobId);
  return data;
});

/**
 * Create an accommodation (placement)
 */
export const createAccommodation = createAsyncThunk<any, void, { state: RootState }>(
  'newJmr/createAccommodation',
  async (_, { getState }) => {
    const state = getState();
    const backgroundFormData: BackgroundFormData = state.newJmrPage.backgroundFormData;
    const jmrFlowType = state.newJmrPage.jmrFlowType;
    const placementId = state.newJmrPage.placementId;
    const selectedTasks = state.newJmrPage.selectedTasks;
    const selectedLimitations = state.newJmrPage.selectedLimitations;
    const checkedResults = state.newJmrPage.jmrResults.filter((jmrResult: TableDataRow) => jmrResult.isChecked);
    const email: string = state.newJmrPage.approvalFormData.emailRecipients;
    // Data from date picker is recorded in local date time
    // this converts it to utc.
    const utcRestrictionStartDate = datePickerStringToDateObj(
      backgroundFormData.restrictionStartDate,
    )?.toISOString() as string;
    const utcRestrictionEndDate =
      backgroundFormData.site.participatingInDecisionHubPilot && backgroundFormData.indefiniteRestrictions
        ? ''
        : (datePickerStringToDateObj(backgroundFormData.restrictionEndDate)?.toISOString() as string);
    // Initialize data
    const accommodationData: AccommodationFields = {
      associateFullName: backgroundFormData.fullName,
      requesterLogin: state.user.alias,
      startAt: utcRestrictionStartDate,
      endAt: utcRestrictionEndDate,
      managerFullName: backgroundFormData.managerName,
      managerAlias: backgroundFormData.managerAlias,
      employeeId: backgroundFormData.employeeID,
      personId: backgroundFormData.personId,
      job: backgroundFormData.job,
      login: backgroundFormData.alias,
      fc: backgroundFormData.siteLocation,
      siteId: backgroundFormData.site.id,
      accommodationType: backgroundFormData.caseSource.toUpperCase(),
      externalCaseId: backgroundFormData.caseID,
      weeklyLimitedHours: +backgroundFormData.limitedHoursPerWeek!,
      modifiedShiftLength: backgroundFormData.limitedHoursPerDay
        ? +backgroundFormData.limitedHoursPerDay
        : +backgroundFormData.shiftLength,
      canWorkNormalShiftHours: !backgroundFormData.cannotWorkNormalShiftHrs,
      canWorkMoreThan40Hours: !backgroundFormData.cannotWorkMoreThan40Hrs,
      // TODO we only know shift is present due to it being required by the useForm<BackgroundFormData> validators. We
      // should refactor this arrangement so that the useForm values are only kept in local state and only stored onto
      // Redux once validated. The redux type should be more restrictive and use type narrowing similar to validation
      // logic to assert that the more restrictive constraints are adhered to
      shiftCode: backgroundFormData.shift!,
      normalShiftLength: +backgroundFormData.shiftLength,
      jobLevel: backgroundFormData?.jobLevel,
      workRestrictions: selectedTasks.map((t): JmrWorkRestriction => ({
        taskId: t.taskId,
        /** @legacy remove when taskGrouping is finished */
        hoursRestricted: t.measureUnit === MeasureUnitValues.Bool ? 'No' : t.restrictedValue,
        taskName: t.label,
        bodyLocation: t.bodyLocationValue,
      })),
      nonPhysicalLimitations: selectedLimitations.map((t) => {
        const { nonPhysicalLimitationTask: _, ...payload } = t;
        return payload;
      }),

      allocatedJobShifts: checkedResults.flatMap((result) => {
        if (result.shifts.length > 1) {
          return result.shifts
            .filter((s) => s.isChecked)
            .map((shift) => ({
              jobId: result.jobId,
              shiftId: shift.shiftId,
              allocatedHours: +shift.totalHours,
              jobName: result.name,
              shiftName: shift.shiftCode,
              appliedModifications: (shift.conflicts || []).flatMap((c) =>
                (c.appliedModifications || []).map<AppliedModification>((m) => ({
                  taskId: c.taskId,
                  modificationId: m.modificationId,
                  freeFormText: m.freeFormText,
                })),
              ),
              modificationNotes: shift.modificationNotes,
            }));
        }
        const appliedModifications = (result.conflicts || []).flatMap((c) =>
          (c.appliedModifications || []).map<AppliedModification>((m) => ({
            taskId: c.taskId,
            modificationId: m.modificationId,
            freeFormText: m.freeFormText,
          })),
        );
        return [
          {
            jobId: result.jobId,
            shiftId: result.shiftId,
            allocatedHours: +result.totalHours,
            jobName: result.name,
            shiftName: result.shiftCode,
            appliedModifications,
            modificationNotes: result.modificationNotes,
          },
        ];
      }),
    };
    if (jmrFlowType === JmrFlowTypes.CreateNewAccommodation) {
      const { data } = await JmrService.createAccommodation({
        accommodation: accommodationData,
        emailRecipients: email,
      });
      return data;
    } else {
      const updatePayload = {
        accommodation: {
          ...accommodationData,
          id: placementId,
        } as any,
        emailRecipients: email,
      };
      if (jmrFlowType === JmrFlowTypes.NewRestrictions) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.NEW_RESTRICTIONS_RECEIVED;
        const { data } = await JmrService.updateAccommodationRestrictions(updatePayload);
        return data;
      } else if (jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.ALTERNATIVE_PLACEMENT_RECOMMENDED;
        const { data } = await JmrService.recommendAlternativePlacement(updatePayload);
        return data;
      } else if (jmrFlowType === JmrFlowTypes.CompleteTransfer) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.TRANSFER_PENDING;
        const { data } = await JmrService.completeTransferPlacement(updatePayload);
        return data;
      } else if (jmrFlowType === JmrFlowTypes.UpdateDates) {
        updatePayload.accommodation.accommodationStatus = ACCOMMODATION_STATUS.ALTERNATIVE_PLACEMENT_RECOMMENDED;
        const { data } = await JmrService.updateRestrictionDates(updatePayload);
        return data;
        // For both create or update PPOW
      } else if (jmrFlowType === JmrFlowTypes.ProviderPlacedOffWork) {
        const { data } = await JmrService.providerPlacedOffWork(updatePayload);
        return data;
      }
    }
  },
);

/**
 * Get placement detail data
 */
export const getPlacementDetails = createAsyncThunk('newJmr/getPlacementDetails', async (accommodationId: number) => {
  const { data: existingAccommodation } = await JmrService.getAccommodation(accommodationId);
  const { data: latestEmployeeInfo } = !!existingAccommodation.personId
    ? await PeopleApiService.getEmployeeInfoByPersonId(existingAccommodation.personId)
    : // personId gets backfilled from the backend but we add this fallback just to be safe
      await PeopleApiService.getEmployeeInfo(existingAccommodation.login);
  return { existingAccommodation, latestEmployeeInfo };
});

/**
 * Get email settings by site
 */
export const getEmailSettingsForSite = createAsyncThunk<EmailSettings, void, { state: RootState }>(
  'newJmr/getEmailSettings',
  async (_, { getState }) => {
    const { backgroundFormData } = getState().newJmrPage;
    const { data } = await EmailDistributionService.getForSite(backgroundFormData.site?.id);
    return data;
  },
);

/**
 * Generate job matches
 */
export const generateJobMatches = createAsyncThunk<
  {
    jmrResultsData: JmrResult[];
    jobCapacityUsageData: SiteJobCapacityUsageData[];
  },
  ApiVersion | undefined,
  { state: RootState }
>('newJmr/generateJobMatches', async (apiVersion = 'v1', { getState }) => {
  const newJmrPageState = getState().newJmrPage;
  const { ldap } = getState().user;
  const formData = _cloneDeep(newJmrPageState.backgroundFormData);
  // Force T2+ experience if in the LDAP group and flag is toggled
  if (isT2Admin(ldap) && newJmrPageState.t2AdminToggle !== null) {
    formData.jobLevel = newJmrPageState.t2AdminToggle ? '2' : '1';
  }
  const payload: JmrPayload = {
    location: formData?.siteLocation,
    siteId: formData.site?.id,
    jobLevel: formData?.jobLevel,
    shiftLength: formData.cannotWorkNormalShiftHrs ? +formData.limitedHoursPerDay! : +formData.shiftLength,
    taskRestrictions: newJmrPageState.selectedTasks?.map((t) => ({
      taskId: t.taskId,
      measureUnit: t.measureUnit,
      /** @legacy remove when taskGrouping is finished */
      restrictedValue: t.measureUnit === MeasureUnitValues.Bool ? YesNoEnum.No : +t.restrictedValue,
    })),
  };
  const { data: jmrResultsData } =
    apiVersion === 'v2' ? await JmrService.generateJobMatchesV2(payload) : await JmrService.generateJobMatches(payload);
  const { data: jobCapacityUsageData } = await CapacityUsageService.getCapacityUsageForSite(payload.siteId);
  return {
    jmrResultsData,
    jobCapacityUsageData,
  };
});

// **** Setup **** //

const { reducer, actions } = createSlice({
  name: 'newJmrPageSlice',
  initialState,
  reducers: {
    clearAustinCases: (state) => {
      state.austinCases = [];
      state.areAustinCasesFetched = false;
    },
    clearProcessingMessagesData: (state) => {
      state.processingMessages = [];
      state.externalCaseId = '';
      state.accommodationType = '';
    },
    setBackgroundFormData: (state, action: PayloadAction<Partial<BackgroundFormData>>) => {
      const newBackgroundState = { ...state.backgroundFormData, ...action.payload };
      // Ensure data fields have consistent values. when the cannotWorkNormalShiftHrs
      // checkbox is unchecked, the limitedHoursPerDay value should be cleared.
      if (!newBackgroundState.cannotWorkNormalShiftHrs) {
        // Set the value to undefined instead of null or "", so that +limitedHoursPerDay
        // evaluates to NaN if never set on UI. NaN becomes null when submitted to backend
        // from UI correctly, but null or "" becomes 0, distorting the user intention
        newBackgroundState.limitedHoursPerDay = undefined;
      }
      // Same for cannotWorkMoreThan40Hrs and limitedHoursPerWeek
      if (!newBackgroundState.cannotWorkMoreThan40Hrs) {
        newBackgroundState.limitedHoursPerWeek = undefined;
      }
      state.backgroundFormData = newBackgroundState;
    },
    setBackgroundFormErrors: (state, action) => {
      state.backgroundFormErrors = action.payload;
    },
    resetBackgroundFormData: (state) => {
      // reset background form
      state.backgroundFormData = initialState.backgroundFormData;
    },
    setPageProgress: (state, action: PayloadAction<JmrPageProgress>) => {
      state.pageProgress = action.payload;
    },
    setIsCancelModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCancelModalOpen = action.payload;
    },
    setSelectedTasks: (state, action: PayloadAction<JmrTask[]>) => {
      state.selectedTasks = action.payload;
    },
    setRestrictionsFormErrors: (state, action) => {
      state.restrictionsFormErrors = action.payload;
    },
    setJmrResults: (state, action: PayloadAction<TableDataRow[]>) => {
      state.jmrResults = action.payload;
    },
    setJmrFilter: (state, action) => {
      state.jmrFilter = action.payload;
    },
    setApprovalFormData: (state, action) => {
      state.approvalFormData = action.payload;
    },
    setApprovalFormErrors: (state, action) => {
      state.approvalFormErrors = action.payload;
    },
    setIsResultsPageValid: (state, action) => {
      state.isResultsPageValid = action.payload;
    },
    setCreatedAccommodationId: (state, action) => {
      state.createdAccommodationId = action.payload;
    },
    setPlacementId: (state, action) => {
      state.placementId = action.payload;
    },
    setJmrFlowType: (state, action) => {
      state.jmrFlowType = action.payload;
    },
    setSearchedAlias: (state, action) => {
      state.searchedAlias = action.payload;
    },
    setIsAUSTINDown: (state, action) => {
      state.isAUSTINDown = action.payload;
    },
    setShowCaseIdRequiredText: (state, action) => {
      state.showCaseIdRequiredText = action.payload;
    },
    setIsProviderPlacedOffWorkModalOpen: (state, action) => {
      state.isProviderPlacedOffWorkModalOpen = action.payload;
    },
    setAccommodationSavingStatus: (state, action) => {
      state.accommodationSavingStatus = action.payload;
    },
    setIsTldAlertModalOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        jobId?: number;
        resultId?: string;
      }>,
    ) => {
      state.tldAlertModal = {
        ...state.tldAlertModal,
        ...(action.payload.open
          ? {
              open: true,
              jobId: action.payload?.jobId || -1,
              resultId: action.payload?.resultId || '',
            }
          : {
              open: false,
              jobId: -1,
              resultId: '',
            }),
      };
    },
    resetTldAlertModal: (state) => {
      state.tldAlertModal = {
        jobId: -1,
        resultId: '',
        open: false,
      };
    },
    setTldAlertModalAcknowledged: (state) => {
      state.tldAlertModalAcknowledged = true;
    },
    resetTldAlertModalAcknowledged: (state) => {
      state.tldAlertModalAcknowledged = false;
    },
    setTldWarningModalAcknowledged: (state) => {
      state.tldWarningModalAcknowledged = true;
    },
    resetTldWarningModalAcknowledged: (state) => {
      state.tldWarningModalAcknowledged = false;
    },
    resetJmrFilter: (state) => {
      state.jmrFilter = initialState.jmrFilter;
    },
    /** For @restrictionsPagelegacy */
    setRestrictionListItems: (state, action) => {
      state.restrictionListItems = action.payload;
    },
    setRestrictionListItem: (state, action) => {
      const newList = state.restrictionListItems;
      newList[action.payload.index] = action.payload.item;
      state.restrictionListItems = newList;
    },
    setConsolidatedRegionTasks: (state, action) => {
      state.consolidatedRegionTasks = action.payload;
    },
    setIsRestrictionsPageReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isRestrictionsPageReadOnly = action.payload;
    },
    deleteRestrictionListItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      const items = state.restrictionListItems;
      state.restrictionListItems = items.filter((_, i) => i !== index);
    },
    /** For restrictionsPage with TaskGrouping see @restrictionsPagelegacy */

    setSelectedRestrictions: (state, action: PayloadAction<RestrictionOption[]>) => {
      state.selectedRestrictions = action.payload;
    },
    setSelectedLimitations: (state, action: PayloadAction<NonPhysicalLimitation[]>) => {
      state.selectedLimitations = action.payload;
    },
    /** end-of restrictionsPage with TaskGrouping */
    setIsUsingNewExperience: (state, action: PayloadAction<boolean>) => {
      state.isUsingNewExperience = action.payload;
    },
    setCurrentPlacementJobShifts: (state, action: PayloadAction<AllocatedJobShift[]>) => {
      state.currentPlacementJobShifts = action.payload;
    },
    setT2Mode: (state, { payload }: PayloadAction<boolean | null>) => {
      state.t2AdminToggle = payload;
    },
  },
  extraReducers: (builder) => {
    /** For @restrictionsPagelegacy */
    builder.addCase(getRegionTasksById.pending, (state) => {
      state.isFetchingRegionTasksById = true;
    });
    builder.addCase(getRegionTasksById.fulfilled, (state, { payload }) => {
      const regionTasks = payload.map((t: RegionTask) => ({ ...t }));
      state.consolidatedRegionTasks = regionTaskListToRestrictionItemTasks(regionTasks);
      state.isFetchingRegionTasksById = false;
    });
    builder.addCase(getRegionTasksById.rejected, (state) => {
      state.isFetchingRegionTasksById = false;
    });
    /** End-of restrictionsPage with TaskGrouping */
    builder.addCase(fetchAustinCases.pending, (state) => {
      state.isLoadingIncident = true;
      state.areAustinCasesFetched = false;
    });
    builder.addCase(fetchAustinCases.fulfilled, (state, { payload }) => {
      state.areAustinCasesFetched = true;
      state.isAUSTINDown = false;
      state.isLoadingIncident = false;
      state.austinCases = payload;
    });
    builder.addCase(fetchAustinCases.rejected, (state) => {
      state.areAustinCasesFetched = false;
      state.isAUSTINDown = true; // This allows us to complete JMR flow when Austin is down
      state.isLoadingIncident = false;
    });
    builder.addCase(getEmployeeInfo.pending, (state) => {
      state.isLoadingPAPI = true;
    });
    builder.addCase(getEmployeeInfo.fulfilled, (state, { payload }: PayloadAction<EmployeeInfo>) => {
      state.backgroundFormData.fullName = `${payload.basicInfo?.firstName} ${payload.basicInfo?.lastName}`;
      state.backgroundFormData.employeeID = payload.basicInfo?.legacyEmployeeId;
      state.backgroundFormData.personId = payload.basicInfo?.personId;
      state.backgroundFormData.job = getJobTitle(payload);
      state.backgroundFormData.managerAlias = payload.basicInfo?.managerLogin;
      state.backgroundFormData.managerName = payload.basicInfo?.managerName;
      state.backgroundFormData.shift = payload.job?.workScheduleId?.trim();
      state.backgroundFormData.jobLevel = payload?.job?.jobLevel;
      // TODO this needs to update the site property with info from site slice. This API request should likely happen
      // outside of this reducer
      state.backgroundFormData.siteLocation = payload.workLocation?.buildingCode;
      state.isLoadingPAPI = false;
    });
    builder.addCase(getEmployeeInfo.rejected, (state) => {
      state.backgroundFormData.fullName = '';
      state.backgroundFormData.employeeID = '';
      state.backgroundFormData.personId = '';
      state.backgroundFormData.job = '';
      state.backgroundFormData.managerAlias = '';
      state.backgroundFormData.managerName = '';
      state.backgroundFormData.shift = '';
      state.backgroundFormData.siteLocation = '';
      state.isLoadingPAPI = false;
    });
    builder.addCase(generateJobMatches.fulfilled, (state, { payload }) => {
      const { jmrResultsData, jobCapacityUsageData } = payload;
      const capacityUsageMap = new Map(
        jobCapacityUsageData.map((jobCapacityUsage) => [jobCapacityUsage.jobId, jobCapacityUsage]),
      );
      state.jmrResults = jmrResultsData.map((jmrResult: JmrResult) =>
        jmrResultToTableRow(jmrResult, capacityUsageMap, state.currentPlacementJobShifts, state.isUsingNewExperience),
      );
    });
    builder.addCase(getJob.fulfilled, (state, { payload }) => {
      state.jmrResults = state.jmrResults.map((jmrResult: TableDataRow) =>
        jmrResult.jobId === payload.id ? { ...jmrResult, jobRequirements: payload.jobRequirements } : jmrResult,
      );
    });
    builder.addCase(createAccommodation.fulfilled, (state, { payload }) => {
      if (payload) {
        state.createdAccommodationId = payload.id;
        state.accommodationSavingStatus = ApiCallStatus.Success;
        state.processingMessages = payload.processingMessages;
        state.externalCaseId = payload.externalCaseId;
        state.accommodationType = payload.accommodationType;
        // reset background form
        state.backgroundFormData = initialState.backgroundFormData;
      }
    });
    builder.addCase(createAccommodation.pending, (state) => {
      state.accommodationSavingStatus = ApiCallStatus.Pending;
    });
    builder.addCase(createAccommodation.rejected, (state) => {
      state.accommodationSavingStatus = ApiCallStatus.Error;
    });
    builder.addCase(getPlacementDetails.pending, (state) => {
      state.isLoadingGetPlacementDetails = true;
    });
    builder.addCase(getPlacementDetails.rejected, (state) => {
      state.isLoadingGetPlacementDetails = false;
    });
    builder.addCase(
      getPlacementDetails.fulfilled,
      (state, { payload: { existingAccommodation, latestEmployeeInfo } }) => {
        const doesDateRepresentIndefiniteRestrictions =
          state.dpFeatureFlag && existingAccommodation.endAt.split('T')[0] === '3000-02-02';
        state.isLoadingGetPlacementDetails = false;
        state.currentPlacementJobShifts = existingAccommodation.allocatedJobShifts;
        state.decisionHubLink = existingAccommodation.decisionHubLink;
        state.backgroundFormData = {
          ...state.backgroundFormData,
          alias: latestEmployeeInfo.basicInfo.login || existingAccommodation.login,
          // this is the site they are currently placed at, do not override with PAPI data
          siteLocation: existingAccommodation.fc,
          site: existingAccommodation.site,
          // We intentionally leave these ID fields unchanged as they should remain stable
          employeeID: existingAccommodation.employeeId,
          personId: existingAccommodation.personId,
          shiftLength: existingAccommodation.normalShiftLength,
          // If cannot work normal shift hours checkbox is unchecked, there should not be a value for its text field
          limitedHoursPerDay: existingAccommodation.canWorkNormalShiftHours
            ? undefined
            : existingAccommodation.modifiedShiftLength?.toString(),
          caseSource: existingAccommodation.accommodationType,
          caseID: existingAccommodation.externalCaseId,
          caseIdValid: existingAccommodation.caseIdValid,
          cannotWorkNormalShiftHrs: !existingAccommodation.canWorkNormalShiftHours,
          cannotWorkMoreThan40Hrs: !existingAccommodation.canWorkMoreThan40Hours,
          // This value controls the indefinite restrictions checkbox
          indefiniteRestrictions: doesDateRepresentIndefiniteRestrictions,
          // If cannot work more than 40 hours checkbox is unchecked, there should not be a value for its text field
          limitedHoursPerWeek: existingAccommodation.canWorkMoreThan40Hours
            ? undefined
            : existingAccommodation.weeklyLimitedHours?.toString(),
          accommodationStatus: existingAccommodation.accommodationStatus.status,
          // latest PAPI fields
          fullName: `${latestEmployeeInfo.basicInfo?.firstName} ${latestEmployeeInfo.basicInfo?.lastName}`,
          job: getJobTitle(latestEmployeeInfo),
          managerAlias: latestEmployeeInfo.basicInfo?.managerLogin,
          managerName: latestEmployeeInfo.basicInfo?.managerName,
          // this is home shift, not placement shift
          shift: latestEmployeeInfo.job?.workScheduleId?.trim() || existingAccommodation.shiftCode,
          jobLevel: latestEmployeeInfo?.job?.jobLevel,
        };

        if (
          [
            JmrFlowTypes.AlternativePlacementRecommended,
            JmrFlowTypes.UpdateDates,
            JmrFlowTypes.CompleteTransfer,
          ].includes(state.jmrFlowType)
        ) {
          if (JmrFlowTypes.CompleteTransfer !== state.jmrFlowType) {
            state.backgroundFormData = {
              ...state.backgroundFormData,
              // End date will be empty if current status is indefinite restrictions.
              // Refer: https://issues.amazon.com/issues/P113097040
              restrictionEndDate:
                existingAccommodation.accommodationStatusId === AccommodationStatusIds.INDEFINITE_RESTRICTIONS ||
                doesDateRepresentIndefiniteRestrictions
                  ? ''
                  : existingAccommodation.endAt.split('T')[0],
              restrictionStartDate: existingAccommodation.startAt.split('T')[0],
            };
          }

          if (
            JmrFlowTypes.CompleteTransfer !== state.jmrFlowType ||
            (JmrFlowTypes.CompleteTransfer === state.jmrFlowType &&
              existingAccommodation.previousSite?.regionId === existingAccommodation.site.regionId)
          ) {
            state.selectedTasks = existingAccommodation.workRestrictions.map(
              (restriction) =>
                ({
                  taskId: restriction.taskId,
                  label: restriction.task?.label,
                  restrictedValue: restriction.hoursRestricted,
                  measureUnit: restriction.task?.measureUnit.name,
                  bodyLocationValue: restriction.bodyLocation,
                }) as JmrTask,
            );
            state.selectedLimitations = existingAccommodation.nonPhysicalLimitations;
          }
        }
      },
    );
    builder.addCase(getEmailSettingsForSite.fulfilled, (state, { payload }) => {
      const backgroundFormData: BackgroundFormData = state.backgroundFormData;
      const managerEmail = `${backgroundFormData.managerAlias}@amazon.com`;
      let emailRecipients = payload?.placementEmailRecipients ?? '';
      if (payload?.supervisorsForPlacementEmail) {
        emailRecipients =
          emailRecipients.trim().length > 0
            ? `${managerEmail};${payload?.placementEmailRecipients}`
            : `${managerEmail}`;
      }
      state.approvalFormData.emailRecipients = emailRecipients;
    });
    // Can be removed once decision portal is live.
    builder.addCase(setDecisionPortalFeatureFlagInNewJmrSlice.fulfilled, (state, { payload }) => {
      state.dpFeatureFlag = payload;
    });
  },
});

// **** Export **** //

export const {
  clearProcessingMessagesData,
  setPageProgress,
  setIsCancelModalOpen,
  setBackgroundFormData,
  setBackgroundFormErrors,
  resetBackgroundFormData,
  setSelectedTasks,
  setConsolidatedRegionTasks,
  setRestrictionsFormErrors,
  setJmrResults,
  setJmrFilter,
  setApprovalFormData,
  setIsResultsPageValid,
  setCreatedAccommodationId,
  setApprovalFormErrors,
  setPlacementId,
  setJmrFlowType,
  setSearchedAlias,
  setIsAUSTINDown,
  setShowCaseIdRequiredText,
  setIsProviderPlacedOffWorkModalOpen,
  setAccommodationSavingStatus,
  setIsTldAlertModalOpen,
  resetTldAlertModal,
  setTldAlertModalAcknowledged,
  resetTldAlertModalAcknowledged,
  setTldWarningModalAcknowledged,
  resetTldWarningModalAcknowledged,
  resetJmrFilter,
  setRestrictionListItems,
  setSelectedRestrictions,
  setSelectedLimitations,
  setRestrictionListItem,
  deleteRestrictionListItem,
  setIsRestrictionsPageReadOnly,
  setIsUsingNewExperience,
  setCurrentPlacementJobShifts,
  setT2Mode,
} = actions;

export default reducer;
